import { useRef } from 'react';
import { useInstance } from '../support/ioc/useInstance';
import { State, useSubscription } from '@autopylot-internal/state';
import { usePageView } from '../support/tracking/usePageView.tsx';
import { Field, FieldList } from '../components/field/Field.tsx';
import { TextInput } from '../components/field/TextInput.tsx';
import { FAA_NAME_PATTERN } from '../profile/ProfilePage.tsx';
import { Button } from '../components/button/Button.tsx';
import { OperatorState } from '../profile/OperatorState.ts';
import { IOperatorProfile } from '../profile/IOperator.ts';
import { AlertPresenter } from '../app/AlertBinder.tsx';
import { TrackingService } from '../support/tracking/TrackingService.ts';
import { AddressField } from '../components/field/AddressField.tsx';

class SetupOperatorState extends State<{ values: IOperatorProfile, meta: {[key in keyof IOperatorProfile]: any}, submitting: boolean }> {

    static inject = () => [OperatorState, TrackingService];
    constructor(private operatorState: OperatorState, private tracker: TrackingService) {
        super({
            values: { ...operatorState.state },
            meta: {},
            submitting: false
        });

        operatorState.subscribe(() => {
            this.setState({
                values: { ...operatorState.state },
            });
        });
    }

    skipSetupOperator = () => {
        this.tracker.track('Pilot Info Do Later Tapped');
        this.operatorState.skipSetupOperator();
    };

    updateValues = (updates: Partial<IOperatorProfile>) => {
        this.setState({ values: {
            ...this.state.values,
            ...updates
        }});
    };

    updateMeta = (meta: {[key in keyof IOperatorProfile]: any}) => {
        this.setState({ meta: {
            ...this.state.meta,
            ...meta
        }});
    };

    save = async () => {
        const { firstName, lastName, phone, address, businessName } = this.state.values;
        await this.operatorState.save({ firstName, lastName, phone, address, businessName });
        this.tracker.track('Pilot Info Entered', () => {
            const meta: any = { firstName, lastName, phone, address, businessName, from: 'Create Account' };
            if (address) {
                meta.addressManuallyEntered = this.state.meta.address?.manuallyEntered || false;
            }
            return meta;
        });
    };
}

export const SetupOperator: React.FC = () => {
    usePageView('Setup Profile');

    const alert = useInstance(AlertPresenter);

    const pageStateInstance = useInstance(SetupOperatorState);
    const pageState = pageStateInstance.state;
    useSubscription(() => pageStateInstance);

    const formRef = useRef<HTMLFormElement>(null);

    if (!pageState?.values) {
        return null;
    }

    const onChangeHandler = (key: string) => {
        return (value: string, meta?: any) => {
            pageStateInstance.updateValues({ [key]: value });
            if (meta) {
                pageStateInstance.updateMeta({ [key]: meta });
            }
        };
    };

    const onClickContinue = async () => {
        if (pageState.submitting) {
            return;
        }

        pageStateInstance.setState({ submitting: true });

        try {
            if (formRef.current?.reportValidity()) {
                await pageStateInstance.save();
            }
        } catch(e: any) {
            alert.showAndLogError(e);
        }

        pageStateInstance.setState({ submitting: false });
    };

    return (
        <div style={{margin: '80px 20px 0'}}>
            <h4>Please complete the info below</h4>
            <p><i>* First, Last, and Phone are required by the FAA for LAANC Authorization</i></p>

            <form style={{margin: '-5px -15px 20px'}} ref={formRef}>
                <FieldList>

                    <Field dataSet={{type: 'first'}} label="First Name" required>
                        <TextInput
                            value={pageState.values.firstName}
                            onChange={onChangeHandler('firstName')}
                            pattern={FAA_NAME_PATTERN}
                            maxlength={60}
                            title="Must be letters, punctuation, and whitespace"
                        />
                    </Field>

                    <Field dataSet={{type: 'last'}} label="Last Name" required>
                        <TextInput
                            value={pageState.values.lastName}
                            onChange={onChangeHandler('lastName')}
                            pattern={FAA_NAME_PATTERN}
                            maxlength={60}
                            title="Must be letters, punctuation, and whitespace"
                        />
                    </Field>

                    <Field dataSet={{type: 'phone'}} label="Phone Number" required>
                        <TextInput
                            value={pageState.values.phone}
                            onChange={onChangeHandler('phone')}
                            type="tel"
                            inputmode="tel"
                            pattern="[1-9]\d{9}"
                            maxlength={10}
                            title="Must be 10 digits, and cannot start with a zero"
                        />
                    </Field>

                    {/* AddressField incorporates the label, so don't set label on Field */}
                    <Field dataSet={{type: 'address'}}>
                        <AddressField
                            value={pageState.values.address}
                            onChange={onChangeHandler('address')}
                        />
                    </Field>


                    <Field dataSet={{type: 'business'}} label="Business Name">
                        <TextInput
                            value={pageState.values.businessName}
                            onChange={onChangeHandler('businessName')}
                        />
                    </Field>

                </FieldList>
            </form>

            <div style={{display: 'flex', flexDirection: 'column', gap: 15, textAlign: 'center'}}>
                <Button expand="block" dataType="continue" disabled={pageState.submitting} onClick={onClickContinue}>
                    Continue
                </Button>
                <Button fill="clear" type="button" size="small" onClick={pageStateInstance.skipSetupOperator} dataType="do-this-later">
                    <b style={{fontSize: '110%'}}>Do this later</b>
                </Button>
            </div>
        </div>

    );
};
